import {
  FeatureComparisonBillingMode,
  FeatureComparisonPriceDisplayMode,
  FeatureComparisonSimpleBilling,
  FeatureComparisonSimpleConfig,
  FeatureComparisonSimpleFeature,
  FeatureComparisonSimplePlan,
  FeatureComparisonTranslatedContent,
  FeatureComparisonTranslatedField,
  FeatureComparisonTranslatedFeature,
  FeatureComparisonTranslatedPlan,
  ResolvedFeatureComparisonTranslation,
  FeatureComparisonValidationIssue,
  FeatureComparisonValidationResult,
} from './feature-comparison.types';

const FEATURE_COMPARISON_SCHEMA = 'feature-comparison-simple';
const FEATURE_COMPARISON_COMPONENT_TYPE_ID = 28;
const FEATURE_COMPARISON_TYPE_PATTERN = /(feature\s*comparison|comparison\s*table|comparative\s*table|tabla\s*comparativa|tabla\s*de\s*comparaci[oó]n|comparaci[oó]n\s*de\s*(features|planes))/i;

function sanitizeString(value: unknown): string {
  if (value == null) {
    return '';
  }

  return String(value).trim();
}

function parseTranslatedContent(rawTranslatedContent: unknown): any | null {
  if (!rawTranslatedContent) {
    return null;
  }

  if (typeof rawTranslatedContent === 'string') {
    try {
      return JSON.parse(rawTranslatedContent);
    } catch (error) {
      console.error('Invalid feature comparison translatedContent payload:', error);
      return null;
    }
  }

  return typeof rawTranslatedContent === 'object' ? rawTranslatedContent : null;
}

function pushIssue(
  issues: FeatureComparisonValidationIssue[],
  step: 1 | 2 | 3 | 4,
  path: string,
  message: string
): void {
  issues.push({ step, path, message });
}

export function createFeatureComparisonId(prefix: 'plan' | 'feature'): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createDefaultPlan(order: number): FeatureComparisonSimplePlan {
  return {
    id: createFeatureComparisonId('plan'),
    name: `Plan ${order}`,
    description: '',
    monthlyPrice: '',
    annualPrice: '',
    currency: '$',
    priceSuffix: '',
    redirectLabel: '',
    redirectUrl: '',
    featured: false,
    order,
  };
}

export function createDefaultFeature(order: number, planIds: string[]): FeatureComparisonSimpleFeature {
  return {
    id: createFeatureComparisonId('feature'),
    label: `Beneficio ${order}`,
    order,
    includedByPlanId: planIds.reduce((acc, planId) => {
      acc[planId] = false;
      return acc;
    }, {} as Record<string, boolean>),
    detailByPlanId: planIds.reduce((acc, planId) => {
      acc[planId] = '';
      return acc;
    }, {} as Record<string, string>),
  };
}

function createRawDefaultFeatureComparisonConfig(): FeatureComparisonSimpleConfig {
  const firstPlan = createDefaultPlan(1);
  const secondPlan = createDefaultPlan(2);

  firstPlan.name = 'Basico';
  secondPlan.name = 'Plus';

  return {
    schema: FEATURE_COMPARISON_SCHEMA,
    version: 1,
    title: 'Comparación de planes',
    subtitle: '',
    featureColumnTitle: 'Beneficio',
    billing: {
      priceDisplayMode: 'toggle',
      defaultMode: 'monthly',
      monthlyLabel: 'Mensual',
      annualLabel: 'Anual',
    },
    plans: [firstPlan, secondPlan],
    features: [createDefaultFeature(1, [firstPlan.id, secondPlan.id])],
  };
}

export function createDefaultFeatureComparisonConfig(): FeatureComparisonSimpleConfig {
  return normalizeFeatureComparisonConfig(createRawDefaultFeatureComparisonConfig());
}

function createTranslatedField(value = '', manual = false): FeatureComparisonTranslatedField {
  return {
    value: sanitizeString(value),
    manual: manual === true,
  };
}

function cloneTranslatedField(field: FeatureComparisonTranslatedField | null | undefined): FeatureComparisonTranslatedField {
  return createTranslatedField(field?.value, field?.manual);
}

function extractTranslatedFieldValue(rawField: unknown): string {
  if (rawField && typeof rawField === 'object' && 'value' in (rawField as Record<string, unknown>)) {
    return sanitizeString((rawField as Record<string, unknown>)['value']);
  }

  return sanitizeString(rawField);
}

function extractTranslatedFieldManual(rawField: unknown): boolean {
  return !!(rawField && typeof rawField === 'object' && (rawField as Record<string, unknown>)['manual'] === true);
}

function normalizeTranslatedField(rawField: unknown, fallbackValue = ''): FeatureComparisonTranslatedField {
  const value = extractTranslatedFieldValue(rawField) || sanitizeString(fallbackValue);
  return createTranslatedField(value, extractTranslatedFieldManual(rawField));
}

function hasTranslatedFieldContent(rawField: unknown): boolean {
  return extractTranslatedFieldValue(rawField).length > 0;
}

function hasAnyManualField(translation: FeatureComparisonTranslatedContent): boolean {
  if (translation.title.manual || translation.subtitle.manual || translation.featureColumnTitle.manual) {
    return true;
  }

  if (translation.billing.monthlyLabel.manual || translation.billing.annualLabel.manual) {
    return true;
  }

  if (translation.plans.some((plan) =>
    plan.name.manual ||
    plan.description.manual ||
    plan.priceSuffix.manual ||
    plan.redirectLabel.manual
  )) {
    return true;
  }

  return translation.features.some((feature) =>
    feature.label.manual ||
    Object.values(feature.detailByPlanId || {}).some((detail) => detail.manual)
  );
}

export function createEmptyFeatureComparisonTranslatedContent(): FeatureComparisonTranslatedContent {
  return {
    title: createTranslatedField(),
    subtitle: createTranslatedField(),
    featureColumnTitle: createTranslatedField(),
    billing: {
      monthlyLabel: createTranslatedField(),
      annualLabel: createTranslatedField(),
    },
    plans: [],
    features: [],
  };
}

export function cloneFeatureComparisonTranslatedContent(value: FeatureComparisonTranslatedContent): FeatureComparisonTranslatedContent {
  return {
    title: cloneTranslatedField(value.title),
    subtitle: cloneTranslatedField(value.subtitle),
    featureColumnTitle: cloneTranslatedField(value.featureColumnTitle),
    billing: {
      monthlyLabel: cloneTranslatedField(value.billing?.monthlyLabel),
      annualLabel: cloneTranslatedField(value.billing?.annualLabel),
    },
    plans: Array.isArray(value.plans)
      ? value.plans.map((plan) => ({
        id: sanitizeString(plan.id),
        name: cloneTranslatedField(plan.name),
        description: cloneTranslatedField(plan.description),
        priceSuffix: cloneTranslatedField(plan.priceSuffix),
        redirectLabel: cloneTranslatedField(plan.redirectLabel),
      }))
      : [],
    features: Array.isArray(value.features)
      ? value.features.map((feature) => ({
        id: sanitizeString(feature.id),
        label: cloneTranslatedField(feature.label),
        detailByPlanId: Object.keys(feature.detailByPlanId || {}).reduce((acc, planId) => {
          acc[planId] = cloneTranslatedField(feature.detailByPlanId[planId]);
          return acc;
        }, {} as Record<string, FeatureComparisonTranslatedField>),
      }))
      : [],
  };
}

export function buildFeatureComparisonTranslationFallback(config: FeatureComparisonSimpleConfig): FeatureComparisonTranslatedContent {
  return {
    title: createTranslatedField(config.title, false),
    subtitle: createTranslatedField(config.subtitle, false),
    featureColumnTitle: createTranslatedField(config.featureColumnTitle, false),
    billing: {
      monthlyLabel: createTranslatedField(config.billing.monthlyLabel, false),
      annualLabel: createTranslatedField(config.billing.annualLabel, false),
    },
    plans: config.plans.map((plan) => ({
      id: plan.id,
      name: createTranslatedField(plan.name, false),
      description: createTranslatedField(plan.description, false),
      priceSuffix: createTranslatedField(plan.priceSuffix, false),
      redirectLabel: createTranslatedField(plan.redirectLabel, false),
    })),
    features: config.features.map((feature) => ({
      id: feature.id,
      label: createTranslatedField(feature.label, false),
      detailByPlanId: config.plans.reduce((acc, plan) => {
        acc[plan.id] = createTranslatedField(
          feature.includedByPlanId?.[plan.id] === true ? feature.detailByPlanId?.[plan.id] : '',
          false
        );
        return acc;
      }, {} as Record<string, FeatureComparisonTranslatedField>),
    })),
  };
}

function normalizeTranslatedPlan(rawPlan: any): FeatureComparisonTranslatedPlan {
  return {
    id: sanitizeString(rawPlan?.id),
    name: normalizeTranslatedField(rawPlan?.name),
    description: normalizeTranslatedField(rawPlan?.description),
    priceSuffix: normalizeTranslatedField(rawPlan?.priceSuffix),
    redirectLabel: normalizeTranslatedField(rawPlan?.redirectLabel),
  };
}

function normalizeTranslatedFeature(rawFeature: any): FeatureComparisonTranslatedFeature {
  const rawDetails = rawFeature?.detailByPlanId && typeof rawFeature.detailByPlanId === 'object'
    ? rawFeature.detailByPlanId
    : {};

  return {
    id: sanitizeString(rawFeature?.id),
    label: normalizeTranslatedField(rawFeature?.label),
    detailByPlanId: Object.keys(rawDetails).reduce((acc, planId) => {
      acc[planId] = normalizeTranslatedField(rawDetails[planId]);
      return acc;
    }, {} as Record<string, FeatureComparisonTranslatedField>),
  };
}

function resolveAutomaticFieldValue(
  currentField: FeatureComparisonTranslatedField | undefined,
  initialField: FeatureComparisonTranslatedField | undefined,
  fallbackValue: string
): string {
  if (currentField && !currentField.manual && sanitizeString(currentField.value)) {
    return sanitizeString(currentField.value);
  }

  if (initialField && !initialField.manual && sanitizeString(initialField.value)) {
    return sanitizeString(initialField.value);
  }

  return sanitizeString(fallbackValue);
}

function syncTranslatedField(
  currentField: FeatureComparisonTranslatedField | undefined,
  initialField: FeatureComparisonTranslatedField | undefined,
  fallbackValue: string
): FeatureComparisonTranslatedField {
  if (currentField?.manual) {
    return createTranslatedField(currentField.value, true);
  }

  return createTranslatedField(resolveAutomaticFieldValue(currentField, initialField, fallbackValue), false);
}

export function syncFeatureComparisonTranslation(
  config: FeatureComparisonSimpleConfig,
  translation: FeatureComparisonTranslatedContent,
  initialTranslation?: FeatureComparisonTranslatedContent | null
): FeatureComparisonTranslatedContent {
  const fallback = buildFeatureComparisonTranslationFallback(config);
  const normalized = cloneFeatureComparisonTranslatedContent(translation);
  const normalizedInitial = initialTranslation ? cloneFeatureComparisonTranslatedContent(initialTranslation) : null;

  const plansById = new Map<string, FeatureComparisonTranslatedPlan>();
  normalized.plans.forEach((plan) => {
    if (plan.id && !plansById.has(plan.id)) {
      plansById.set(plan.id, plan);
    }
  });

  const featuresById = new Map<string, FeatureComparisonTranslatedFeature>();
  normalized.features.forEach((feature) => {
    if (feature.id && !featuresById.has(feature.id)) {
      featuresById.set(feature.id, feature);
    }
  });

  const initialPlansById = new Map<string, FeatureComparisonTranslatedPlan>();
  normalizedInitial?.plans.forEach((plan) => {
    if (plan.id && !initialPlansById.has(plan.id)) {
      initialPlansById.set(plan.id, plan);
    }
  });

  const initialFeaturesById = new Map<string, FeatureComparisonTranslatedFeature>();
  normalizedInitial?.features.forEach((feature) => {
    if (feature.id && !initialFeaturesById.has(feature.id)) {
      initialFeaturesById.set(feature.id, feature);
    }
  });

  return {
    title: syncTranslatedField(normalized.title, normalizedInitial?.title, fallback.title.value),
    subtitle: syncTranslatedField(normalized.subtitle, normalizedInitial?.subtitle, fallback.subtitle.value),
    featureColumnTitle: syncTranslatedField(
      normalized.featureColumnTitle,
      normalizedInitial?.featureColumnTitle,
      fallback.featureColumnTitle.value
    ),
    billing: {
      monthlyLabel: syncTranslatedField(
        normalized.billing?.monthlyLabel,
        normalizedInitial?.billing?.monthlyLabel,
        fallback.billing.monthlyLabel.value
      ),
      annualLabel: syncTranslatedField(
        normalized.billing?.annualLabel,
        normalizedInitial?.billing?.annualLabel,
        fallback.billing.annualLabel.value
      ),
    },
    plans: fallback.plans.map((fallbackPlan) => {
      const translatedPlan = plansById.get(fallbackPlan.id);
      const initialPlan = initialPlansById.get(fallbackPlan.id);
      return {
        id: fallbackPlan.id,
        name: syncTranslatedField(translatedPlan?.name, initialPlan?.name, fallbackPlan.name.value),
        description: syncTranslatedField(translatedPlan?.description, initialPlan?.description, fallbackPlan.description.value),
        priceSuffix: syncTranslatedField(translatedPlan?.priceSuffix, initialPlan?.priceSuffix, fallbackPlan.priceSuffix.value),
        redirectLabel: syncTranslatedField(translatedPlan?.redirectLabel, initialPlan?.redirectLabel, fallbackPlan.redirectLabel.value),
      };
    }),
    features: fallback.features.map((fallbackFeature) => {
      const translatedFeature = featuresById.get(fallbackFeature.id);
      const initialFeature = initialFeaturesById.get(fallbackFeature.id);
      const baseFeature = config.features.find((feature) => feature.id === fallbackFeature.id);
      const detailByPlanId = config.plans.reduce((acc, plan) => {
        if (baseFeature?.includedByPlanId?.[plan.id] !== true) {
          acc[plan.id] = createTranslatedField();
          return acc;
        }

        acc[plan.id] = syncTranslatedField(
          translatedFeature?.detailByPlanId?.[plan.id],
          initialFeature?.detailByPlanId?.[plan.id],
          fallbackFeature.detailByPlanId?.[plan.id]?.value || ''
        );
        return acc;
      }, {} as Record<string, FeatureComparisonTranslatedField>);

      return {
        id: fallbackFeature.id,
        label: syncTranslatedField(translatedFeature?.label, initialFeature?.label, fallbackFeature.label.value),
        detailByPlanId,
      };
    }),
  };
}

function hasMeaningfulFeatureComparisonTranslation(rawEnglish: any, rawFeatureComparison: any): boolean {
  const translatedPlans = Array.isArray(rawFeatureComparison?.plans) ? rawFeatureComparison.plans : [];
  const translatedFeatures = Array.isArray(rawFeatureComparison?.features) ? rawFeatureComparison.features : [];

  if (
    hasTranslatedFieldContent(rawEnglish?.title) ||
    hasTranslatedFieldContent(rawEnglish?.subtitle) ||
    hasTranslatedFieldContent(rawFeatureComparison?.title) ||
    hasTranslatedFieldContent(rawFeatureComparison?.subtitle) ||
    hasTranslatedFieldContent(rawFeatureComparison?.featureColumnTitle) ||
    hasTranslatedFieldContent(rawFeatureComparison?.billing?.monthlyLabel) ||
    hasTranslatedFieldContent(rawFeatureComparison?.billing?.annualLabel)
  ) {
    return true;
  }

  return translatedPlans.some((plan: any) =>
    hasTranslatedFieldContent(plan?.name) ||
    hasTranslatedFieldContent(plan?.description) ||
    hasTranslatedFieldContent(plan?.priceSuffix) ||
    hasTranslatedFieldContent(plan?.redirectLabel)
  ) || translatedFeatures.some((feature: any) =>
    hasTranslatedFieldContent(feature?.label) ||
    Object.values(feature?.detailByPlanId || {}).some((detail) => hasTranslatedFieldContent(detail))
  );
}

export function buildFeatureComparisonTranslationState(
  rawTranslatedContent: unknown,
  config: FeatureComparisonSimpleConfig
): ResolvedFeatureComparisonTranslation {
  const fallback = buildFeatureComparisonTranslationFallback(config);
  const parsed = parseTranslatedContent(rawTranslatedContent);
  const rawEnglish = parsed?.en;
  const rawFeatureComparison = rawEnglish?.featureComparison;

  if (!rawEnglish || typeof rawEnglish !== 'object' || !rawFeatureComparison || typeof rawFeatureComparison !== 'object') {
    return {
      value: fallback,
      hasExistingTranslation: false,
      isManual: false,
    };
  }

  const rawPlans = Array.isArray(rawFeatureComparison.plans)
    ? rawFeatureComparison.plans.map((plan: any) => normalizeTranslatedPlan(plan))
    : [];
  const rawFeatures = Array.isArray(rawFeatureComparison.features)
    ? rawFeatureComparison.features.map((feature: any) => normalizeTranslatedFeature(feature))
    : [];

  const synced = syncFeatureComparisonTranslation(config, {
    title: normalizeTranslatedField(rawFeatureComparison.title ?? rawEnglish.title, fallback.title.value),
    subtitle: normalizeTranslatedField(rawFeatureComparison.subtitle ?? rawEnglish.subtitle, fallback.subtitle.value),
    featureColumnTitle: normalizeTranslatedField(rawFeatureComparison.featureColumnTitle, fallback.featureColumnTitle.value),
    billing: {
      monthlyLabel: normalizeTranslatedField(rawFeatureComparison.billing?.monthlyLabel, fallback.billing.monthlyLabel.value),
      annualLabel: normalizeTranslatedField(rawFeatureComparison.billing?.annualLabel, fallback.billing.annualLabel.value),
    },
    plans: rawPlans,
    features: rawFeatures,
  });

  return {
    value: synced,
    hasExistingTranslation: hasMeaningfulFeatureComparisonTranslation(rawEnglish, rawFeatureComparison),
    isManual: hasAnyManualField(synced),
  };
}

export function buildFeatureComparisonTranslatedPayload(value: FeatureComparisonTranslatedContent): { en: any } {
  const normalized = cloneFeatureComparisonTranslatedContent(value);
  const syncedTitle = cloneTranslatedField(normalized.title);
  const syncedSubtitle = cloneTranslatedField(normalized.subtitle);

  return {
    en: {
      manual: hasAnyManualField(normalized),
      title: syncedTitle,
      subtitle: syncedSubtitle,
      featureComparison: {
        title: syncedTitle,
        subtitle: syncedSubtitle,
        featureColumnTitle: cloneTranslatedField(normalized.featureColumnTitle),
        billing: {
          monthlyLabel: cloneTranslatedField(normalized.billing.monthlyLabel),
          annualLabel: cloneTranslatedField(normalized.billing.annualLabel),
        },
        plans: normalized.plans.map((plan) => ({
          id: plan.id,
          name: cloneTranslatedField(plan.name),
          description: cloneTranslatedField(plan.description),
          priceSuffix: cloneTranslatedField(plan.priceSuffix),
          redirectLabel: cloneTranslatedField(plan.redirectLabel),
        })),
        features: normalized.features.map((feature) => ({
          id: feature.id,
          label: cloneTranslatedField(feature.label),
          detailByPlanId: Object.keys(feature.detailByPlanId || {}).reduce((acc, planId) => {
            acc[planId] = cloneTranslatedField(feature.detailByPlanId[planId]);
            return acc;
          }, {} as Record<string, FeatureComparisonTranslatedField>),
        })),
      },
    },
  };
}

export function shouldSendFeatureComparisonTranslatedContent(
  currentValue: FeatureComparisonTranslatedContent,
  initialValue: FeatureComparisonTranslatedContent,
  hasExistingTranslation: boolean
): boolean {
  if (hasExistingTranslation) {
    return true;
  }

  return JSON.stringify(cloneFeatureComparisonTranslatedContent(currentValue)) !== JSON.stringify(cloneFeatureComparisonTranslatedContent(initialValue));
}

export function mergeFeatureComparisonTranslatedTexts(
  config: FeatureComparisonSimpleConfig,
  translation: FeatureComparisonTranslatedContent | null | undefined
): FeatureComparisonSimpleConfig {
  if (!translation) {
    return normalizeFeatureComparisonConfig(config);
  }

  const syncedTranslation = syncFeatureComparisonTranslation(config, translation);
  const translatedPlansById = new Map<string, FeatureComparisonTranslatedPlan>(
    syncedTranslation.plans.map((plan) => [plan.id, plan])
  );
  const translatedFeaturesById = new Map<string, FeatureComparisonTranslatedFeature>(
    syncedTranslation.features.map((feature) => [feature.id, feature])
  );

  return normalizeFeatureComparisonConfig({
    ...config,
    title: sanitizeString(syncedTranslation.title.value) || config.title,
    subtitle: sanitizeString(syncedTranslation.subtitle.value) || config.subtitle,
    featureColumnTitle: sanitizeString(syncedTranslation.featureColumnTitle.value) || config.featureColumnTitle,
    billing: {
      ...config.billing,
      monthlyLabel: sanitizeString(syncedTranslation.billing.monthlyLabel.value) || config.billing.monthlyLabel,
      annualLabel: sanitizeString(syncedTranslation.billing.annualLabel.value) || config.billing.annualLabel,
    },
    plans: config.plans.map((plan) => {
      const translatedPlan = translatedPlansById.get(plan.id);
      return {
        ...plan,
        name: sanitizeString(translatedPlan?.name.value) || plan.name,
        description: sanitizeString(translatedPlan?.description.value) || plan.description,
        priceSuffix: sanitizeString(translatedPlan?.priceSuffix.value) || plan.priceSuffix,
        redirectLabel: sanitizeString(translatedPlan?.redirectLabel.value) || plan.redirectLabel,
      };
    }),
    features: config.features.map((feature) => {
      const translatedFeature = translatedFeaturesById.get(feature.id);
      return {
        ...feature,
        label: sanitizeString(translatedFeature?.label.value) || feature.label,
        detailByPlanId: config.plans.reduce((acc, plan) => {
          acc[plan.id] = feature.includedByPlanId?.[plan.id] === true
            ? sanitizeString(translatedFeature?.detailByPlanId?.[plan.id]?.value)
            : '';
          return acc;
        }, {} as Record<string, string>),
      };
    }),
  });
}

function normalizePlan(rawPlan: any, index: number): FeatureComparisonSimplePlan {
  return {
    id: sanitizeString(rawPlan?.id) || createFeatureComparisonId('plan'),
    name: sanitizeString(rawPlan?.name),
    description: sanitizeString(rawPlan?.description),
    monthlyPrice: sanitizeString(rawPlan?.monthlyPrice),
    annualPrice: sanitizeString(rawPlan?.annualPrice),
    currency: sanitizeString(rawPlan?.currency),
    priceSuffix: sanitizeString(rawPlan?.priceSuffix),
    redirectLabel: sanitizeString(rawPlan?.redirectLabel),
    redirectUrl: sanitizeString(rawPlan?.redirectUrl),
    featured: rawPlan?.featured === true,
    order: Number(rawPlan?.order || index + 1),
  };
}

function normalizeBilling(rawConfig: any): FeatureComparisonSimpleBilling {
  const rawBilling = rawConfig?.billing;
  const rawBillingToggle = rawConfig?.billingToggle;

  let priceDisplayMode: FeatureComparisonPriceDisplayMode = 'monthly';

  if (rawBilling?.priceDisplayMode === 'annual' || rawBilling?.priceDisplayMode === 'toggle' || rawBilling?.priceDisplayMode === 'monthly') {
    priceDisplayMode = rawBilling.priceDisplayMode;
  } else if (rawBillingToggle) {
    priceDisplayMode = rawBillingToggle?.enabled === true ? 'toggle' : 'monthly';
  }

  return {
    priceDisplayMode,
    defaultMode:
      rawBilling?.defaultMode === 'annual' || rawBillingToggle?.defaultMode === 'annual'
        ? 'annual'
        : 'monthly',
    monthlyLabel: sanitizeString(rawBilling?.monthlyLabel ?? rawBillingToggle?.monthlyLabel) || 'Mensual',
    annualLabel: sanitizeString(rawBilling?.annualLabel ?? rawBillingToggle?.annualLabel) || 'Anual',
  };
}

function normalizeFeature(rawFeature: any, index: number): FeatureComparisonSimpleFeature {
  return {
    id: sanitizeString(rawFeature?.id) || createFeatureComparisonId('feature'),
    label: sanitizeString(rawFeature?.label),
    order: Number(rawFeature?.order || index + 1),
    includedByPlanId: typeof rawFeature?.includedByPlanId === 'object' && rawFeature?.includedByPlanId
      ? { ...rawFeature.includedByPlanId }
      : {},
    detailByPlanId: typeof rawFeature?.detailByPlanId === 'object' && rawFeature?.detailByPlanId
      ? { ...rawFeature.detailByPlanId }
      : {},
  };
}

function normalizeFeaturedPlans(plans: FeatureComparisonSimplePlan[]): FeatureComparisonSimplePlan[] {
  let featuredFound = false;

  return plans.map((plan) => {
    if (!plan.featured) {
      return {
        ...plan,
        featured: false,
      };
    }

    if (!featuredFound) {
      featuredFound = true;
      return {
        ...plan,
        featured: true,
      };
    }

    return {
      ...plan,
      featured: false,
    };
  });
}

export function syncFeatureBooleansWithPlans(config: FeatureComparisonSimpleConfig): FeatureComparisonSimpleConfig {
  const planIds = config.plans.map((plan) => plan.id);

  return {
    ...config,
    features: config.features.map((feature, index) => {
      const includedByPlanId = planIds.reduce((acc, planId) => {
        acc[planId] = feature.includedByPlanId?.[planId] === true;
        return acc;
      }, {} as Record<string, boolean>);
      const detailByPlanId = planIds.reduce((acc, planId) => {
        const detail = feature.detailByPlanId?.[planId];
        acc[planId] = detail == null ? '' : sanitizeString(detail);
        return acc;
      }, {} as Record<string, string>);

      return {
        ...feature,
        order: index + 1,
        includedByPlanId,
        detailByPlanId,
      };
    }),
  };
}

export function normalizeFeatureComparisonConfig(rawConfig: any): FeatureComparisonSimpleConfig {
  const fallback = createRawDefaultFeatureComparisonConfig();
  const rawPlans = Array.isArray(rawConfig?.plans) && rawConfig.plans.length > 0
    ? rawConfig.plans
    : fallback.plans;
  const rawFeatures = Array.isArray(rawConfig?.features) && rawConfig.features.length > 0
    ? rawConfig.features
    : fallback.features;

  const sortedPlans = rawPlans
    .map((plan: any, index: number) => normalizePlan(plan, index))
    .sort((left: FeatureComparisonSimplePlan, right: FeatureComparisonSimplePlan) => left.order - right.order)
    .map((plan: FeatureComparisonSimplePlan, index: number) => ({
      ...plan,
      order: index + 1,
    }));

  const normalizedPlans = normalizeFeaturedPlans(sortedPlans);
  const normalizedFeatures = rawFeatures
    .map((feature: any, index: number) => normalizeFeature(feature, index))
    .sort((left: FeatureComparisonSimpleFeature, right: FeatureComparisonSimpleFeature) => left.order - right.order)
    .map((feature: FeatureComparisonSimpleFeature, index: number) => ({
      ...feature,
      order: index + 1,
    }));

  return syncFeatureBooleansWithPlans({
    schema: FEATURE_COMPARISON_SCHEMA,
    version: 1,
    title: sanitizeString(rawConfig?.title),
    subtitle: sanitizeString(rawConfig?.subtitle),
    featureColumnTitle: sanitizeString(rawConfig?.featureColumnTitle) || 'Beneficio',
    billing: normalizeBilling(rawConfig),
    plans: normalizedPlans,
    features: normalizedFeatures,
  });
}

export function addPlan(config: FeatureComparisonSimpleConfig): FeatureComparisonSimpleConfig {
  const nextPlan = createDefaultPlan(config.plans.length + 1);
  return normalizeFeatureComparisonConfig({
    ...config,
    plans: [...config.plans, nextPlan],
    features: config.features.map((feature) => ({
      ...feature,
      includedByPlanId: {
        ...feature.includedByPlanId,
        [nextPlan.id]: false,
      },
      detailByPlanId: {
        ...feature.detailByPlanId,
        [nextPlan.id]: '',
      },
    })),
  });
}

export function movePlan(
  config: FeatureComparisonSimpleConfig,
  planId: string,
  direction: -1 | 1
): FeatureComparisonSimpleConfig {
  const currentIndex = config.plans.findIndex((plan) => plan.id === planId);
  const targetIndex = currentIndex + direction;

  if (currentIndex < 0 || targetIndex < 0 || targetIndex >= config.plans.length) {
    return config;
  }

  const nextPlans = [...config.plans];
  const [moved] = nextPlans.splice(currentIndex, 1);
  nextPlans.splice(targetIndex, 0, moved);

  return normalizeFeatureComparisonConfig({
    ...config,
    plans: nextPlans,
  });
}

export function removePlanAndCleanupFeatures(
  config: FeatureComparisonSimpleConfig,
  planId: string
): FeatureComparisonSimpleConfig {
  return normalizeFeatureComparisonConfig({
    ...config,
    plans: config.plans.filter((plan) => plan.id !== planId),
    features: config.features.map((feature) => {
      const includedByPlanId = { ...feature.includedByPlanId };
      const detailByPlanId = { ...feature.detailByPlanId };
      delete includedByPlanId[planId];
      delete detailByPlanId[planId];
      return {
        ...feature,
        includedByPlanId,
        detailByPlanId,
      };
    }),
  });
}

export function addFeature(config: FeatureComparisonSimpleConfig): FeatureComparisonSimpleConfig {
  return normalizeFeatureComparisonConfig({
    ...config,
    features: [...config.features, createDefaultFeature(config.features.length + 1, config.plans.map((plan) => plan.id))],
  });
}

export function moveFeature(
  config: FeatureComparisonSimpleConfig,
  featureId: string,
  direction: -1 | 1
): FeatureComparisonSimpleConfig {
  const currentIndex = config.features.findIndex((feature) => feature.id === featureId);
  const targetIndex = currentIndex + direction;

  if (currentIndex < 0 || targetIndex < 0 || targetIndex >= config.features.length) {
    return config;
  }

  const nextFeatures = [...config.features];
  const [moved] = nextFeatures.splice(currentIndex, 1);
  nextFeatures.splice(targetIndex, 0, moved);

  return normalizeFeatureComparisonConfig({
    ...config,
    features: nextFeatures,
  });
}

export function removeFeature(config: FeatureComparisonSimpleConfig, featureId: string): FeatureComparisonSimpleConfig {
  return normalizeFeatureComparisonConfig({
    ...config,
    features: config.features.filter((feature) => feature.id !== featureId),
  });
}

export function resolvePlanPrice(
  plan: FeatureComparisonSimplePlan,
  billing: FeatureComparisonSimpleBilling,
  activeBillingMode?: FeatureComparisonBillingMode
): string {
  if (billing.priceDisplayMode === 'annual') {
    return sanitizeString(plan.annualPrice);
  }

  if (billing.priceDisplayMode === 'toggle') {
    const resolvedMode = activeBillingMode === 'annual' ? 'annual' : activeBillingMode === 'monthly' ? 'monthly' : billing.defaultMode;
    return resolvedMode === 'annual' ? sanitizeString(plan.annualPrice) : sanitizeString(plan.monthlyPrice);
  }

  return sanitizeString(plan.monthlyPrice);
}

export function validateFeatureComparisonConfig(config: FeatureComparisonSimpleConfig): FeatureComparisonValidationResult {
  const issues: FeatureComparisonValidationIssue[] = [];

  if (!sanitizeString(config.title)) {
    pushIssue(issues, 1, 'title', 'El titulo visible es obligatorio.');
  }

  if (!sanitizeString(config.featureColumnTitle)) {
    pushIssue(issues, 1, 'featureColumnTitle', 'El titulo de la primera columna es obligatorio.');
  }

  if (!['monthly', 'annual', 'toggle'].includes(config.billing.priceDisplayMode)) {
    pushIssue(issues, 1, 'billing.priceDisplayMode', 'El modo de precios debe ser mensual, anual o toggle.');
  }

  if (config.billing.priceDisplayMode === 'toggle') {
    if (!sanitizeString(config.billing.monthlyLabel)) {
      pushIssue(issues, 1, 'billing.monthlyLabel', 'El label mensual es obligatorio cuando el modo es toggle.');
    }
    if (!sanitizeString(config.billing.annualLabel)) {
      pushIssue(issues, 1, 'billing.annualLabel', 'El label anual es obligatorio cuando el modo es toggle.');
    }
    if (config.billing.defaultMode !== 'monthly' && config.billing.defaultMode !== 'annual') {
      pushIssue(issues, 1, 'billing.defaultMode', 'El modo por defecto debe ser mensual o anual.');
    }
  }

  if (config.plans.length < 2) {
    pushIssue(issues, 2, 'plans', 'Debes agregar al menos dos planes.');
  }

  const featuredCount = config.plans.filter((plan) => plan.featured).length;
  if (featuredCount > 1) {
    pushIssue(issues, 2, 'plans.featured', 'Solo un plan puede estar marcado como destacado.');
  }

  config.plans.forEach((plan) => {
    if (!sanitizeString(plan.name)) {
      pushIssue(issues, 2, `plan:${plan.id}:name`, 'Cada plan debe tener nombre.');
    }
  });

  if (config.features.length < 1) {
    pushIssue(issues, 3, 'features', 'Debes agregar al menos un beneficio.');
  }

  config.features.forEach((feature) => {
    if (!sanitizeString(feature.label)) {
      pushIssue(issues, 3, `feature:${feature.id}:label`, 'Cada beneficio debe tener nombre.');
    }

    config.plans.forEach((plan) => {
      if (typeof feature.includedByPlanId?.[plan.id] !== 'boolean') {
        pushIssue(issues, 3, `feature:${feature.id}:includedByPlanId`, 'Cada beneficio debe tener un booleano por plan.');
      }

      if (typeof feature.detailByPlanId?.[plan.id] !== 'string') {
        pushIssue(issues, 3, `feature:${feature.id}:detailByPlanId`, 'Cada beneficio debe tener un texto opcional por plan.');
      }
    });
  });

  return {
    isValid: issues.length === 0,
    issues,
  };
}

export function isFeatureComparisonTypeName(value: unknown): boolean {
  return FEATURE_COMPARISON_TYPE_PATTERN.test(sanitizeString(value));
}

export function isFeatureComparisonComponentType(componentTypeId: number, componentTypes: any[], fallbackName = ''): boolean {
  if (Number(componentTypeId || 0) === FEATURE_COMPARISON_COMPONENT_TYPE_ID) {
    return true;
  }

  if (isFeatureComparisonTypeName(fallbackName)) {
    return true;
  }

  const matchedType = componentTypes.find((item) => Number(item?.componentTypeId || 0) === Number(componentTypeId || 0));
  return isFeatureComparisonTypeName(matchedType?.componentTypeName);
}
